import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { ensureMenuImageFile } from '../restaurant/menuImageUtils';

function authHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function appendPayloadToFormData(formData, payload) {
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (key === 'imageFile' || value == null || value === '') return;
    if (typeof value === 'boolean') {
      formData.append(key, value ? '1' : '0');
      return;
    }
    if (key === 'nutrition' && value && typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0 && key === 'ingredientIds') {
        formData.append(key, '[]');
        return;
      }
      value.forEach((entry) => {
        if (entry != null && entry !== '') formData.append(key, String(entry));
      });
      return;
    }
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  });
}

async function postOrPutMasterItem(method, url, payload) {
  const headers = authHeaders();
  const imageFile = payload?.imageFile;

  if (imageFile) {
    const processed = await ensureMenuImageFile(imageFile);
    const formData = new FormData();
    appendPayloadToFormData(formData, payload);
    formData.append('image', processed, processed.name || 'menu-image.webp');
    const res = await axiosRestaurantInstance[method](url, formData, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });
    return res;
  }

  const body = { ...payload };
  delete body.imageFile;
  return axiosRestaurantInstance[method](url, body, { headers });
}

export function normalizeMasterMenuItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    masterFoodItemId: raw.master_food_item_id ?? raw.masterFoodItemId ?? null,
    foodItem: raw.food_item ?? raw.foodItem ?? '',
    foodCategory: raw.food_category ?? raw.foodCategory ?? '',
    foodType: String(raw.food_type ?? raw.foodType ?? 'VEG').toUpperCase(),
    courseRole: String(raw.course_role ?? raw.courseRole ?? '').toUpperCase(),
    description: raw.description ?? '',
    notes: raw.notes ?? '',
    cuisineOrigin: raw.cuisine_origin ?? raw.cuisineOrigin ?? '',
    status: String(raw.status ?? 'ACTIVE').toUpperCase(),
    nutrition: raw.nutrition ?? null,
    ingredients: Array.isArray(raw.ingredients) ? raw.ingredients : [],
    ingredientIds: Array.isArray(raw.ingredient_ids)
      ? raw.ingredient_ids
      : Array.isArray(raw.ingredientIds)
        ? raw.ingredientIds
        : [],
    imagePath: raw.image_path ?? raw.imagePath ?? '',
    imageUrl: raw.image_url ?? raw.imageUrl ?? '',
    alreadyAdopted: Boolean(raw.already_adopted ?? raw.alreadyAdopted),
  };
}

export async function fetchAdminMenuMaster(params = {}) {
  const res = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.menuMaster, {
    headers: authHeaders(),
    params: {
      includeInactive: params.includeInactive ? 'true' : undefined,
      foodCategory: params.foodCategory || undefined,
      foodType: params.foodType || undefined,
      courseRole: params.courseRole || undefined,
      q: params.q || undefined,
    },
  });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  return {
    items: items.map(normalizeMasterMenuItem).filter(Boolean),
    grouped: res.data?.grouped || {},
    foodItemCount: Number(res.data?.foodItemCount) || items.length,
  };
}

export async function fetchRestaurantMenuMaster(params = {}) {
  const res = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantMenuMaster, {
    headers: authHeaders(),
    params: {
      restaurantId: params.restaurantId || undefined,
      menuSource: params.menuSource || undefined,
      foodCategory: params.foodCategory || undefined,
      foodType: params.foodType || undefined,
      courseRole: params.courseRole || undefined,
      q: params.q || undefined,
    },
  });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  return {
    items: items.map(normalizeMasterMenuItem).filter(Boolean),
    grouped: res.data?.grouped || {},
    foodItemCount: Number(res.data?.foodItemCount) || items.length,
  };
}

export async function createMenuMasterItem(payload) {
  const res = await postOrPutMasterItem('post', RESTAURANT_API_PATHS.menuMaster, payload);
  return res.data;
}

export async function updateMenuMasterItem(masterId, payload) {
  const res = await postOrPutMasterItem(
    'put',
    RESTAURANT_API_PATHS.menuMasterById(masterId),
    payload,
  );
  return res.data;
}

export async function deactivateMenuMasterItem(masterId) {
  const res = await axiosRestaurantInstance.delete(
    RESTAURANT_API_PATHS.menuMasterById(masterId),
    { headers: authHeaders() },
  );
  return res.data;
}

export async function adoptFoodItemsFromMaster(restaurantId, payload) {
  const res = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantFoodItemsFromMaster(restaurantId),
    payload,
    { headers: authHeaders() },
  );
  return res.data;
}
